import {useMemo} from 'react'
import {PatientExtended, PatientMedicalInfoFormValidationSchema} from '@/features/patients/types'
import {usePatientEditModeView} from '@/features/patients/hooks/usePatientEditModeView'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {PatientDetailsEditButtons} from '@/features/patients/components/patient-details-edit-buttons/PatientDetailsEditButtons'
import {useTranslation} from '@/translations/i18n'
import {
    getPatientDirtyFieldsFromFormValues,
    remapPatientToDefaultValues,
    remapPatientToPayload
} from '@/features/patients/utils'
import {usePatientFormSubmission} from '@/features/patients/hooks/usePatientFormSubmission'
import {useUpdatePatient} from '@/features/patients/services/useUpdatePatient'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {ObjectKeys} from '@/utilities/helpers'
import {PatientMedicalInfoDetails} from '@/features/patients/components/patient-medical-info-details/PatientMedicalInfoDetails'
import {useOutletContext} from 'react-router-dom'
import {PatientMedicalInfoFields} from '@/features/patients/components/patient-medical-info-fields/PatientMedicalInfoFields'
import {zodResolver} from '@hookform/resolvers/zod'
import {usePermissions} from '@/features/auth/store/store'

export const PatientDetailsMedicalInfoSection = () => {
    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const form = useForm<PatientMedicalInfoFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientMedicalInfoFormValidationSchema),
        defaultValues: remapPatientToDefaultValues(patient)
    })

    return (
        <FormProvider {...form}>
            <PatientDetailsMedicalInfoSectionContent patient={patient} />
        </FormProvider>
    )
}

const PatientDetailsMedicalInfoSectionContent = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = usePatientEditModeView('medical-info')
    const permissions = usePermissions()
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const birthDate = useMemo(() => new Date(patient.birthDate), [patient.birthDate])
    const form = useFormContext<PatientMedicalInfoFormValidationSchema>()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = usePatientFormSubmission({
        formSchema: PatientMedicalInfoFormValidationSchema
    })

    // mutation
    const updatePatient = useUpdatePatient({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getPatientDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapPatientToPayload(dirtyFormValues)
            return updatePatient.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('patients:medical_info')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.patients?.write && (
                    <PatientDetailsEditButtons
                        groupName="medical-info"
                        isConfirmDisabled={ObjectKeys(form.formState.dirtyFields).length == 0}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                    />
                )
            }
        >
            {isEditModeEnabled ? (
                <PatientMedicalInfoFields birthDate={birthDate} />
            ) : (
                <PatientMedicalInfoDetails patient={patient} />
            )}
        </DetailsFieldsSection>
    )
}
